import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API = typeof window !== 'undefined' ? `${window.location.origin}/api` : '/api';

const PIX_KEY_TYPES = [
  { value: 'cpf', label: 'CPF' },
  { value: 'email', label: 'E-mail' },
  { value: 'phone', label: 'Telefone' },
  { value: 'random', label: 'Chave Aleatória' }
];

const BANKS = [
  'Banco do Brasil', 'Bradesco', 'Itaú', 'Santander', 'Caixa Econômica',
  'Nubank', 'Inter', 'C6 Bank', 'PagBank', 'Mercado Pago',
  'Sicoob', 'Sicredi', 'BTG Pactual', 'Safra', 'Original',
  'Neon', 'Next', 'Banrisul', 'Outro'
];

const PixRegistrationBanner = ({ variant = 'banner' }) => {
  const { user } = useAuth();
  const [hasPix, setHasPix] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    pix_key_type: 'cpf',
    pix_key: '',
    holder_name: '',
    holder_document: '',
    bank_name: ''
  });

  const isEligible = user?.role === 'creator' || user?.is_fundador;

  useEffect(() => {
    if (!isEligible) return;
    const fetchBankData = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/users/me/bank-data`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setHasPix(res.data.has_pix === true);
      } catch {
        setHasPix(false);
      }
    };
    fetchBankData();
  }, [isEligible]);

  if (!isEligible || hasPix === null || hasPix === true) return null;

  const formatCPF = (v) => {
    const digits = v.replace(/\D/g, '').slice(0, 11);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0,3)}.${digits.slice(3)}`;
    if (digits.length <= 9) return `${digits.slice(0,3)}.${digits.slice(3,6)}.${digits.slice(6)}`;
    return `${digits.slice(0,3)}.${digits.slice(3,6)}.${digits.slice(6,9)}-${digits.slice(9)}`;
  };

  const formatPhone = (v) => {
    const digits = v.replace(/\D/g, '').slice(0, 11);
    if (digits.length <= 2) return `(${digits}`;
    if (digits.length <= 7) return `(${digits.slice(0,2)}) ${digits.slice(2)}`;
    return `(${digits.slice(0,2)}) ${digits.slice(2,7)}-${digits.slice(7)}`;
  };

  const handleKeyChange = (val) => {
    if (formData.pix_key_type === 'cpf') val = formatCPF(val);
    else if (formData.pix_key_type === 'phone') val = formatPhone(val);
    setFormData(p => ({ ...p, pix_key: val }));
  };

  const handleSubmit = async () => {
    if (!formData.pix_key.trim()) { toast.error('Informe a chave PIX'); return; }
    if (!formData.holder_name.trim()) { toast.error('Informe o nome do destinatário'); return; }
    if (!formData.holder_document.trim()) { toast.error('Informe o CPF do titular'); return; }
    if (!formData.bank_name.trim()) { toast.error('Selecione o banco'); return; }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/users/me/bank-data`, {
        pix_key: formData.pix_key,
        pix_key_type: formData.pix_key_type,
        holder_name: formData.holder_name,
        holder_document: formData.holder_document.replace(/\D/g, ''),
        bank_name: formData.bank_name
      }, { headers: { Authorization: `Bearer ${token}` } });

      toast.success('Conta PIX cadastrada com sucesso!');
      setHasPix(true);
      setShowModal(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar dados PIX');
    } finally {
      setSaving(false);
    }
  };

  const getPlaceholder = () => {
    switch (formData.pix_key_type) {
      case 'cpf': return '000.000.000-00';
      case 'email': return 'seuemail@exemplo.com';
      case 'phone': return '(11) 99999-9999';
      case 'random': return 'Cole sua chave aleatória';
      default: return '';
    }
  };

  const getInputType = () => {
    if (formData.pix_key_type === 'email') return 'email';
    if (formData.pix_key_type === 'phone' || formData.pix_key_type === 'cpf') return 'tel';
    return 'text';
  };

  return (
    <>
      {/* Variante BADGE - notificação discreta com contador */}
      {variant === 'badge' && (
        <button
          type="button"
          onClick={() => setShowModal(true)}
          data-testid="pix-registration-badge"
          title="Cadastre sua conta PIX para receber pagamentos"
          aria-label="Cadastrar conta PIX (pendente)"
          className="relative inline-flex items-center justify-center w-11 h-11 rounded-full bg-emerald-500/15 hover:bg-emerald-500/25 active:scale-95 border border-emerald-500/40 transition-all"
        >
          <svg className="w-5 h-5 text-emerald-400" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M9.37 5.51A7.35 7.35 0 0012 4.5c4.14 0 7.5 3.36 7.5 7.5s-3.36 7.5-7.5 7.5S4.5 16.14 4.5 12c0-1.03.21-2.01.58-2.91" />
            <path d="M12 2v4l3-3" />
          </svg>
          {/* Contador de notificações */}
          <span
            data-testid="pix-badge-counter"
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center border-2 border-[#0D0D0D] leading-none animate-pulse"
          >
            1
          </span>
        </button>
      )}

      {/* Variante BANNER - full-width (retrocompatível) */}
      {variant === 'banner' && (
        <div
          data-testid="pix-registration-banner"
          className="mx-auto max-w-6xl px-4 mb-4"
        >
        <div className="bg-gradient-to-r from-[#1a2a1a] to-[#1a1a0d] border border-emerald-500/40 rounded-2xl p-4 flex items-center gap-3 shadow-lg shadow-emerald-900/10">
          <div className="shrink-0 w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
            <svg className="w-5 h-5 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-emerald-300 font-semibold text-sm leading-tight">
              Cadastre sua conta PIX
            </p>
            <p className="text-gray-400 text-xs mt-0.5 leading-tight">
              Para receber pagamentos das suas vendas
            </p>
          </div>
          <button
            onClick={() => setShowModal(true)}
            data-testid="pix-register-btn"
            className="shrink-0 bg-emerald-500 hover:bg-emerald-600 active:scale-95 text-black text-sm font-bold px-4 py-2 rounded-full transition-all"
          >
            Cadastrar
          </button>
        </div>
      </div>
      )}

      {/* Modal bottom-sheet */}
      {showModal && (
        <div
          className="fixed inset-0 z-[300] flex items-end sm:items-center justify-center"
          onClick={(e) => e.target === e.currentTarget && setShowModal(false)}
        >
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowModal(false)} />

          <div
            data-testid="pix-registration-modal"
            className="relative w-full sm:max-w-md bg-[#141414] border border-white/10 rounded-t-3xl sm:rounded-2xl max-h-[90vh] overflow-y-auto animate-in slide-in-from-bottom duration-300"
          >
            {/* Handle bar mobile */}
            <div className="flex justify-center pt-3 sm:hidden">
              <div className="w-10 h-1 rounded-full bg-white/20" />
            </div>

            {/* Header */}
            <div className="p-5 pb-2">
              <div className="flex items-center gap-3 mb-1">
                <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                  <svg className="w-5 h-5 text-emerald-400" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M9.37 5.51A7.35 7.35 0 0012 4.5c4.14 0 7.5 3.36 7.5 7.5s-3.36 7.5-7.5 7.5S4.5 16.14 4.5 12c0-1.03.21-2.01.58-2.91"/>
                    <path d="M12 2v4l3-3"/>
                  </svg>
                </div>
                <div>
                  <h2 className="text-lg font-bold text-white">Cadastrar conta PIX</h2>
                  <p className="text-xs text-gray-500">Dados para recebimento de vendas</p>
                </div>
              </div>
            </div>

            {/* Form */}
            <div className="px-5 pb-6 space-y-4">
              {/* Tipo de chave */}
              <div>
                <label className="text-xs font-medium text-gray-400 mb-1.5 block">Tipo de Chave PIX</label>
                <div className="grid grid-cols-4 gap-1.5">
                  {PIX_KEY_TYPES.map(t => (
                    <button
                      key={t.value}
                      type="button"
                      data-testid={`pix-type-${t.value}`}
                      onClick={() => setFormData(p => ({ ...p, pix_key_type: t.value, pix_key: '' }))}
                      className={`py-2 rounded-lg text-xs font-semibold transition-all ${
                        formData.pix_key_type === t.value
                          ? 'bg-emerald-500 text-black'
                          : 'bg-white/5 text-gray-400 hover:bg-white/10'
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Chave PIX */}
              <div>
                <label className="text-xs font-medium text-gray-400 mb-1.5 block">Chave PIX</label>
                <input
                  data-testid="pix-key-input"
                  type={getInputType()}
                  value={formData.pix_key}
                  onChange={(e) => handleKeyChange(e.target.value)}
                  placeholder={getPlaceholder()}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder:text-gray-600 focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/30 outline-none transition-all"
                />
              </div>

              {/* Nome do Destinatário */}
              <div>
                <label className="text-xs font-medium text-gray-400 mb-1.5 block">Nome do Destinatário</label>
                <input
                  data-testid="pix-holder-name-input"
                  type="text"
                  value={formData.holder_name}
                  onChange={(e) => setFormData(p => ({ ...p, holder_name: e.target.value }))}
                  placeholder="Nome completo do titular"
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder:text-gray-600 focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/30 outline-none transition-all"
                />
              </div>

              {/* CPF do Titular */}
              <div>
                <label className="text-xs font-medium text-gray-400 mb-1.5 block">CPF do Titular</label>
                <input
                  data-testid="pix-holder-cpf-input"
                  type="tel"
                  value={formData.holder_document}
                  onChange={(e) => setFormData(p => ({ ...p, holder_document: formatCPF(e.target.value) }))}
                  placeholder="000.000.000-00"
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder:text-gray-600 focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/30 outline-none transition-all"
                />
              </div>

              {/* Banco de Origem */}
              <div>
                <label className="text-xs font-medium text-gray-400 mb-1.5 block">Banco de Origem</label>
                <select
                  data-testid="pix-bank-select"
                  value={formData.bank_name}
                  onChange={(e) => setFormData(p => ({ ...p, bank_name: e.target.value }))}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/30 outline-none transition-all appearance-none"
                  style={{ backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`, backgroundPosition: 'right 0.75rem center', backgroundRepeat: 'no-repeat', backgroundSize: '1.25em 1.25em' }}
                >
                  <option value="" className="bg-[#1a1a1a]">Selecione o banco</option>
                  {BANKS.map(b => (
                    <option key={b} value={b} className="bg-[#1a1a1a]">{b}</option>
                  ))}
                </select>
              </div>

              {/* Botões */}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setShowModal(false)}
                  className="flex-1 bg-white/5 hover:bg-white/10 border border-white/10 text-gray-300 py-3 rounded-xl font-semibold text-sm transition-all"
                  data-testid="pix-cancel-btn"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={saving}
                  className="flex-1 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed text-black py-3 rounded-xl font-bold text-sm transition-all active:scale-[0.98]"
                  data-testid="pix-save-btn"
                >
                  {saving ? 'Salvando...' : 'Salvar PIX'}
                </button>
              </div>

              <p className="text-[10px] text-gray-600 text-center leading-tight">
                Seus dados bancários são armazenados com segurança e usados exclusivamente para transferir seus ganhos.
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PixRegistrationBanner;
